import { z } from "zod";
import { ImageMeasurementSchema, ImageVariantSchema } from "./product-visual-details";

/** Only direct current instructions may grant evidence access or refuse attached images. */
export function currentImageInstruction(message: string): string {
  const unquoted = message.replace(/```[\s\S]*?```|“[^”]*”|「[^」]*」|"[^"\n]*"|‘[^’]*’|(?:^|[\s(])'[^'\n]+'(?=$|[\s).,;!?])/g, "");
  return unquoted.split(/[，。；！!?？\n,;.]/).map((clause) => {
    const chineseReport = /(?:(?:图片|照片|截图)(?:中|上|里)?(?:的?文字)?|图(?:中|上|里)(?:的?文字)?|用户)(?:写着|写了|写的是|显示|文字是|说|提到|标着)/.exec(clause);
    const englishReport = /\b(?:images?|photos?|pictures?|screenshots?|users?)\s+(?:(?:text|clearly|literally)\s+)?(?:says?|said|reads?|shows?|states?|wrote)\b/i.exec(clause);
    // Keep a direct instruction before the reported evidence, but never its content.
    return clause.slice(0, Math.min(chineseReport?.index ?? clause.length, englishReport?.index ?? clause.length));
  }).join("，");
}

/**
 * 一轮请求里最多能带几张图。
 *
 * 2026-09-22 从 4 提到 8：1688 商品详情页的成交属性、参数表与详情卖点分散在纵向很长的
 * 页面里，一段视口拍不全，抓成一张长图又会被模型降采样到看不清 12px 小字。
 * 分段截图必须**逐段按原生分辨率**送给模型，所以额度要跟着分段数走。
 *
 * 取 8 而不是更多：`observeImages` 要求模型逐张回一份观察，张数对不上整轮作废
 * （`images.length !== request.images.length`），图越多越容易漏张。
 */
export const MAX_IMAGES_PER_RUN = 8;

/**
 * 一行可见文字 + 它在**页面里的纵向位置**（0 = 页面顶部，1 = 页面底部）。
 *
 * 存在的唯一理由：让"截图上看到的字段名"能与"DOM 里的字段位置"对上。
 * 真机（2026-09-22 Temu 发布页）的缺口：商品名称/详情描述两个 textarea 在 DOM 里
 * 只有通用占位符，`label`/`nearbyText` 都读不到名字，规则永远不命中；
 * 而截图里明明有这两个名字——但 `visibleText` 只是一堆文字行、没有位置，
 * 按词表顺序分配就是猜，填错框（把标题写进描述框）比不填贵得多。
 */
export const ImageObservationLineSchema = z.object({
  text: z.string().trim().min(1).max(500),
  /** 页内归一化纵向位置。0–1 之外、NaN 一律拒掉：坐标错了会让配对错位。 */
  y: z.number().finite().min(0).max(1),
}).strict();
export type ImageObservationLine = z.infer<typeof ImageObservationLineSchema>;

export const ImageObservationItemSchema = z.object({
  index: z.number().int().min(0).max(MAX_IMAGES_PER_RUN - 1),
  objects: z.array(z.string().trim().min(1).max(120)).max(16),
  summary: z.string().trim().min(1).max(1200),
  visibleText: z.array(z.string().trim().min(1).max(500)).max(32),
  /**
   * 带坐标的可见文字行（可选）。
   *
   * **可选**是硬要求：老数据、老客户端、以及"模型没给坐标"的情况都必须照旧通过，
   * 行为与以前完全一致；缺失时调用方退回纯文字匹配，不做任何位置推断。
   */
  visibleLines: z.array(ImageObservationLineSchema).max(60).optional(),
  measurements: z.array(ImageMeasurementSchema).max(10).optional(),
  variants: z.array(ImageVariantSchema).max(20).optional(),
  orientation: z.enum(["upright", "rotated_90", "rotated_180", "rotated_270", "unknown"]),
  readability: z.enum(["clear", "partial", "unreadable"]),
  uncertainties: z.array(z.string().trim().min(1).max(300)).max(12),
  sufficientEvidence: z.boolean(),
}).strict().superRefine((image, ctx) => {
  if (image.sufficientEvidence && (image.readability === "unreadable" || (!image.objects.length && !image.visibleText.length))) {
    ctx.addIssue({ code: "custom", path: ["sufficientEvidence"], message: "可用证据必须来自实际可辨认的对象或文字" });
  }
});
export type ImageObservationItem = z.infer<typeof ImageObservationItemSchema>;

/** Private model evidence. Never transport this object in analytics, logs or status events. */
export const ImageObservationSchema = z.object({
  images: z.array(ImageObservationItemSchema).min(1).max(MAX_IMAGES_PER_RUN),
}).strict().superRefine((observation, ctx) => {
  observation.images.forEach((image, index) => {
    if (image.index !== index) ctx.addIssue({ code: "custom", path: ["images", index, "index"], message: "图片索引必须从 0 开始连续且保持附件顺序" });
  });
});
export type ImageObservation = z.infer<typeof ImageObservationSchema>;

export const ImageAnalysisStatusSchema = z.enum([
  "received", "recognizing", "recognized", "answering", "completed", "recognition_failed", "answer_failed", "cancelled", "skipped",
]);
export type ImageAnalysisStatus = z.infer<typeof ImageAnalysisStatusSchema>;
export const ImageAnalysisEventDataSchema = z.object({
  runId: z.string().min(1), count: z.number().int().min(1).max(MAX_IMAGES_PER_RUN), status: ImageAnalysisStatusSchema,
}).strict();
export type ImageAnalysisEventData = z.infer<typeof ImageAnalysisEventDataSchema>;
